<template>
  <!-- Déclencheur : a l'apparence d'un champ de recherche, ouvre la palette.
       Remplace l'ancien champ décoratif du topbar (qui ne faisait rien). -->
  <button type="button" class="gs-trigger" @click="openPalette" aria-label="Recherche globale (Cmd+K)">
    <Icon name="search" :size="16" class="gs-tr-icon" aria-hidden="true" />
    <span class="gs-tr-ph">Aller à une page, chercher de l'aide…</span>
    <kbd class="gs-kbd" aria-hidden="true">{{ isMac ? '⌘' : 'Ctrl' }} K</kbd>
  </button>

  <Teleport to="body">
    <!-- Pas de <transition> : une palette doit apparaître/disparaître
         instantanément (et les transitions interrompues peuvent laisser
         l'overlay bloqué à l'écran). -->
    <div v-if="open" class="gs-overlay" @mousedown.self="close">
        <div class="gs-panel" role="dialog" aria-modal="true" aria-label="Recherche globale">
          <div class="gs-inputwrap">
            <Icon name="search" :size="17" aria-hidden="true" />
            <input
              ref="inputEl"
              v-model="q"
              type="text"
              placeholder="Tapez une page (« liste noire ») ou une question (« transférer un appel »)…"
              aria-label="Rechercher une page ou une aide"
              @keydown="onKey"
            />
            <kbd class="gs-kbd">Échap</kbd>
          </div>

          <div ref="listEl" class="gs-results">
            <template v-if="pageResults.length">
              <div class="gs-caption">Pages</div>
              <button
                v-for="(r, i) in pageResults"
                :key="'p' + r.to"
                type="button"
                class="gs-item"
                :class="{ selected: selected === i }"
                :data-index="i"
                @mouseenter="selected = i"
                @click="goPage(r)"
              >
                <span class="gs-ic"><Icon :name="r.icon" :size="15" /></span>
                <span class="gs-label">{{ r.label }}</span>
                <span v-if="r.group" class="gs-tag">{{ r.group }}</span>
              </button>
            </template>

            <template v-if="docResults.length">
              <div class="gs-caption">Dans l'aide</div>
              <button
                v-for="(r, i) in docResults"
                :key="'d' + r.sectionId + r.article.id"
                type="button"
                class="gs-item"
                :class="{ selected: selected === pageResults.length + i }"
                :data-index="pageResults.length + i"
                @mouseenter="selected = pageResults.length + i"
                @click="goDoc(r)"
              >
                <span class="gs-ic help"><Icon name="help-circle" :size="15" /></span>
                <span class="gs-label">
                  {{ r.article.title }}
                  <small v-if="r.article.summary">{{ r.article.summary }}</small>
                </span>
              </button>
            </template>

            <div v-if="q.trim() && !pageResults.length && !docResults.length" class="gs-empty">
              Aucun résultat pour «&nbsp;{{ q }}&nbsp;».
              <button type="button" class="gs-link" @click="goHelp">Ouvrir le centre d'aide</button>
            </div>
          </div>

          <div class="gs-foot">
            <span><kbd>↑</kbd><kbd>↓</kbd> naviguer</span>
            <span><kbd>Entrée</kbd> ouvrir</span>
            <span><kbd>Échap</kbd> fermer</span>
          </div>
        </div>
      </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Icon from '@/shared/components/Icon.vue'
import { useNav, type NavItem } from '@/shared/composables/useNav'
import { useDocs } from '@/modules/docs/composables/useDocs'
import type { DocArticle } from '@/modules/docs/content/types'

const router = useRouter()
const { groups } = useNav()
const { search: searchDocs } = useDocs()

const open = ref(false)
const q = ref('')
const selected = ref(0)
const inputEl = ref<HTMLInputElement | null>(null)
const listEl = ref<HTMLDivElement | null>(null)
const isMac = navigator.platform.toUpperCase().includes('MAC')

/** Minuscules sans accents : « rendez-vous » matche « Rendez-vous », « rdv »… */
function fold(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

type PageResult = NavItem & { group?: string }

/** Pages visibles (rôle déjà filtré par useNav), avec le libellé de leur groupe. */
const allPages = computed<PageResult[]>(() =>
  groups.value.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label }))),
)

const pageResults = computed<PageResult[]>(() => {
  const needle = fold(q.value.trim())
  // Palette vide = plan du site : toutes les pages auxquelles j'ai accès.
  if (!needle) return allPages.value
  return allPages.value.filter((p) => {
    const hay = fold(`${p.label} ${(p.keywords ?? []).join(' ')}`)
    return hay.includes(needle)
  })
})

const docResults = computed<{ sectionId: string; article: DocArticle }[]>(() =>
  q.value.trim() ? searchDocs(q.value).slice(0, 6) : [],
)

const total = computed(() => pageResults.value.length + docResults.value.length)

watch([q, open], () => { selected.value = 0 })

function openPalette() {
  open.value = true
  q.value = ''
  nextTick(() => inputEl.value?.focus())
}
function close() {
  open.value = false
}

function goPage(r: PageResult) {
  close()
  router.push(r.to)
}
function goDoc(r: { sectionId: string; article: DocArticle }) {
  close()
  router.push(`/aide/${r.sectionId}/${r.article.id}`)
}
function goHelp() {
  close()
  router.push('/aide')
}

function scrollSelectedIntoView() {
  nextTick(() => {
    listEl.value
      ?.querySelector(`[data-index="${selected.value}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  })
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (total.value) selected.value = (selected.value + 1) % total.value
    scrollSelectedIntoView()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (total.value) selected.value = (selected.value - 1 + total.value) % total.value
    scrollSelectedIntoView()
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const i = selected.value
    if (i < pageResults.value.length) {
      const p = pageResults.value[i]
      if (p) goPage(p)
    } else {
      const d = docResults.value[i - pageResults.value.length]
      if (d) goDoc(d)
    }
  } else if (e.key === 'Escape') {
    e.preventDefault()
    close()
  }
}

/** Raccourci global ⌘K / Ctrl+K — le raccourci affiché est enfin réel. */
function onGlobalKey(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault()
    if (open.value) close()
    else openPalette()
  } else if (e.key === 'Escape' && open.value) {
    close()
  }
}

onMounted(() => document.addEventListener('keydown', onGlobalKey))
onUnmounted(() => document.removeEventListener('keydown', onGlobalKey))
</script>

<style scoped>
/* ── Déclencheur (aspect champ de recherche) ── */
.gs-trigger {
  position: relative;
  flex: 1;
  max-width: 400px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: var(--surface-hover);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-size: 13px;
  color: var(--text-muted);
  cursor: pointer;
  text-align: left;
  transition: background var(--duration-base), border-color var(--duration-base);
}
.gs-trigger:hover { background: var(--surface-active); border-color: var(--border-strong); }
.gs-trigger:focus-visible { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px rgba(124,58,237,0.14); }
.gs-tr-icon { flex-shrink: 0; }
.gs-tr-ph { flex: 1; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.gs-kbd {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--text-muted);
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-xs);
  padding: 1px 5px;
  letter-spacing: 0;
  flex-shrink: 0;
}

/* ── Palette ── */
.gs-overlay {
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.42);
  backdrop-filter: blur(2px);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 96px 16px 16px;
}
.gs-panel {
  width: 100%;
  max-width: 620px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.gs-inputwrap {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px;
  border-bottom: 1px solid var(--border);
  color: var(--text-muted);
}
.gs-inputwrap input {
  flex: 1;
  border: none;
  outline: none;
  background: none;
  font-size: 14.5px;
  color: var(--text);
}
.gs-inputwrap input::placeholder { color: var(--text-muted); }

.gs-results { max-height: 380px; overflow-y: auto; padding: 6px; }
.gs-caption {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 8px 10px 4px;
}
.gs-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 13.5px;
  color: var(--text);
  cursor: pointer;
  text-align: left;
}
.gs-item.selected { background: var(--surface-hover); }
.gs-ic {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  border-radius: var(--radius-sm);
  background: rgba(124,58,237,0.10);
  color: var(--primary);
  flex-shrink: 0;
}
.gs-ic.help { background: var(--surface-active); color: var(--text-secondary); }
.gs-label { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.gs-label small { color: var(--text-muted); font-size: 11.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gs-tag {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--text-muted);
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  padding: 1px 8px;
  flex-shrink: 0;
}
.gs-empty { padding: 22px 14px; text-align: center; color: var(--text-muted); font-size: 13px; }
.gs-link {
  display: inline;
  border: none;
  background: none;
  color: var(--primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 2px;
  text-decoration: underline;
}
.gs-foot {
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  border-top: 1px solid var(--border);
  color: var(--text-muted);
  font-size: 11.5px;
}
.gs-foot kbd {
  font-family: var(--font-mono);
  font-size: 10px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-xs);
  padding: 0 4px;
  margin-right: 3px;
}

</style>
